/**
 * Validation Utility Functions
 * Real-time validation for questionnaire responses
 */

import { DEFAULT_VALIDATION_RULES, QUESTION_VALIDATION_RULES } from './validationRules';

/**
 * Validate a single value against a set of rules
 * @param {string} value - Value to validate
 * @param {Array} rules - Array of validation rules
 * @returns {object} { isValid: boolean, errors: Array<string> }
 */
export const validateValue = (value, rules = []) => {
  const errors = [];

  for (const rule of rules) {
    if (!rule.validate(value)) {
      errors.push(rule.message);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get validation rules for a specific question
 * @param {string} questionId - Question ID
 * @param {boolean} isRequired - Whether the question is required
 * @returns {Array} Array of validation rules
 */
export const getValidationRules = (questionId, isRequired = false) => {
  // Get question-specific rules
  const questionRules = QUESTION_VALIDATION_RULES[questionId] || QUESTION_VALIDATION_RULES.default || [];
  
  // Add required rule if needed and not already present
  if (isRequired && !questionRules.some(rule => rule.type === 'required')) {
    return [DEFAULT_VALIDATION_RULES.required, ...questionRules];
  }
  
  return questionRules;
};

/**
 * Validate a questionnaire response
 * @param {string} questionId - Question ID
 * @param {string} value - Response value
 * @param {boolean} isRequired - Whether the question is required
 * @returns {object} { isValid: boolean, errors: Array<string> }
 */
export const validateResponse = (questionId, value, isRequired = false) => {
  const rules = getValidationRules(questionId, isRequired);
  return validateValue(value, rules);
};

/**
 * Validate multiple responses (for cross-field validation)
 * @param {object} responses - Object with questionId as keys and response values
 * @param {Array} crossFieldRules - Array of cross-field validation rules
 * @returns {object} { isValid: boolean, errors: object }
 */
export const validateMultipleResponses = (responses, crossFieldRules = []) => {
  const errors = {};
  let isValid = true;

  // Run cross-field validations
  for (const rule of crossFieldRules) {
    const result = rule.validate(responses);
    if (!result.isValid) {
      isValid = false;
      // Add errors to affected questions
      for (const questionId of rule.affectedQuestions) {
        if (!errors[questionId]) {
          errors[questionId] = [];
        }
        errors[questionId].push(result.message);
      }
    }
  }

  return {
    isValid,
    errors
  };
};

/**
 * Cross-field validation rules
 * Example rules for validating relationships between questions
 */
export const CROSS_FIELD_RULES = [
  // Example: End date must be after start date
  {
    name: 'date_range',
    affectedQuestions: ['start_date', 'end_date'],
    validate: (responses) => {
      const startDate = responses.start_date ? new Date(responses.start_date) : null;
      const endDate = responses.end_date ? new Date(responses.end_date) : null;
      
      if (!startDate || !endDate) {
        return { isValid: true };
      }
      
      if (endDate <= startDate) {
        return {
          isValid: false,
          message: 'End date must be after start date'
        };
      }
      
      return { isValid: true };
    }
  },
  
  // Example: Total percentages must equal 100
  {
    name: 'percentage_total',
    affectedQuestions: ['equity_percentage', 'debt_percentage', 'other_percentage'],
    validate: (responses) => {
      const equity = parseFloat(responses.equity_percentage) || 0;
      const debt = parseFloat(responses.debt_percentage) || 0;
      const other = parseFloat(responses.other_percentage) || 0;
      
      const total = equity + debt + other;
      
      if (total > 0 && Math.abs(total - 100) > 0.01) {
        return {
          isValid: false,
          message: 'Total percentages must equal 100%'
        };
      }
      
      return { isValid: true };
    }
  }
];

/**
 * Check whether a value is a usable http(s) URL.
 *
 * Needed because `meeting_link` is not guaranteed to be a URL: a Recall orphan
 * meeting on a platform with no URL builder (Webex/GoToMeeting/Chime) stores the
 * bare conference id there, and rendering that as an `<a href>` would produce a
 * broken relative link. Follows the `url` rule in validationRules.js, plus an
 * explicit protocol assertion so values like `mailto:` / `uup-goan-ugf` are rejected.
 *
 * @param {string} value - Value to check
 * @returns {boolean} True when the value parses as an http: or https: URL
 */
export const isHttpUrl = (value) => {
  if (!value || typeof value !== 'string') return false;
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Debounce function for real-time validation
 * @param {function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

